import React, { useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable as LexicalContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin as LexicalRichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HashtagPlugin as LexicalHashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { ListPlugin as LexicalListPlugin } from '@lexical/react/LexicalListPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';

import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import ToolbarPlugin from './plugins/IVAToolbarPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';
import TreeViewPlugin from './plugins/TreeViewPlugin';
import OnchangePlugin, { Data } from './plugins/OnchangePlugin';
import UploadPlugin from './plugins/UploadPlugin';
import KeyBoardPlugin from './plugins/KeyBoardPlugin';
import MentionPlugin from './plugins/MentionPlugin';
import FocusPlugin from './plugins/FocusPlugin';
import config from './config';
import { PaperPlane } from 'src/components/icons';

export type Props = {
  placeholder?: string;
  showDebug?: boolean;
  size?: 'large' | 'small';
  onChange?: (html: string, data: Data) => void;
  html?: string | null;
  onSend?: (html: string) => void | Promise<void>;
  onEsc?: () => void;
  shouldFocus?: boolean;
};

const ChatBox: React.FC<Props> = (props) => {
  const {
    placeholder,
    showDebug = false,
    size = 'small',
    onChange,
    html,
    onSend,
    onEsc,
    shouldFocus,
  } = props;

  const htmlValue = useRef<string | null | undefined>(html);
  const [sent, setSent] = useState<number>(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  return (
    <LexicalComposer initialConfig={config}>
      <div className="iva-editor-wrapper">
        <ToolbarPlugin
          undoAndRedo={false}
          textBlock={false}
          insert={false}
          link={false}
        />
        <LexicalRichTextPlugin
          ErrorBoundary={LexicalErrorBoundary}
          contentEditable={
            <div className="iva-editor-wrapper-chatbox" ref={wrapperRef}>
              <LexicalContentEditable
                spellCheck
                className={`iva-editor-chatbox ${size}`}
              />
              <div
                className="iva-editor-send"
                onClick={() => {
                  if (htmlValue.current) {
                    onSend?.(htmlValue.current);
                    setSent((send) => send + 1);
                    htmlValue.current = '';
                  }
                }}
              >
                <PaperPlane />
              </div>
            </div>
          }
          placeholder={
            <div className="iva-editor-placeholder">{placeholder}</div>
          }
        />
        <CodeHighlightPlugin />
        <OnchangePlugin
          key={sent}
          onChange={(value, data) => {
            onChange?.(value, data);
            htmlValue.current = value;
          }}
          html={htmlValue.current}
          shouldEmpty
        />
        <LexicalHashtagPlugin />
        <HistoryPlugin />
        <AutoLinkPlugin />
        <LexicalListPlugin />
        <UploadPlugin />
        <KeyBoardPlugin onEsc={onEsc} />
        <MentionPlugin />
        <FocusPlugin scrollRef={wrapperRef} shouldFocus={shouldFocus} />
        {showDebug && <TreeViewPlugin />}
      </div>
    </LexicalComposer>
  );
};

export default ChatBox;
